/**
 * DisplayLabParameter
 *
 * A React component that renders a lab result value along with a visual indicator
 * (via CSS class) representing its clinical status — such as 'normal', 'warning', or 'abnormal'.
 *
 * The component:
 * - Expects a `record` object with a `.value` property.
 * - Uses `GetLabResultClass()` to determine the correct classification based on lab reference ranges.
 * - Returns `null` if the record is a title row (i.e., `ele.title` is truthy).
 * - Wraps the result value in a styled `<div>` with the corresponding class name.
 *
 * Classification is handled externally using the utility `GetLabResultClass(ele)` which analyzes:
 * - `refmin`, `refmax`: normal range
 * - `lbound`, `ubound`: extended acceptable range
 * - `reftype`: to apply numeric classification only when applicable
 *
 * Expected structure of `record`:
 * {
 *   value: {
 *     value: string | number,
 *     title?: boolean,
 *     reftype?: string,
 *     refmin?: number,
 *     refmax?: number,
 *     lbound?: number,
 *     ubound?: number
 *   }
 * }
 *
 * @param {Object} record - The record object representing a lab parameter result.
 * @returns {JSX.Element|null} A styled <div> with the lab value, or null for title rows.
 */
export default function DisplayLabParameter(record) {
    let ele = record.value;
    let rawValue = ele?.value?.value ?? '';
    let value = rawValue !== '' ? rawValue : '';

    if (ele.title) return null;

    const resultClassName = GetLabResultClass(ele);

    return <div className={`result-display ${resultClassName}`}>{value}</div>;
}

/**
 * Classifies a lab result item as 'normal', 'warning', 'abnormal', or '' (unclassified).
 *
 * Classification Logic:
 * - Applies different rules for numeric (`reftype === 'NUMERIC'`) and non-numeric values.
 *
 * For Numeric (`reftype === 'NUMERIC'`):
 * - If both `lbound` and `ubound` are `-1`:
 *   - If value ∈ [`refmin`, `refmax`] → `'normal'`
 *   - Else → `'warning'`
 * - If bounds are defined:
 *   - If value ∈ [`refmin`, `refmax`] → `'normal'`
 *   - If value slightly outside but within `lbound` or `ubound` → `'warning'`
 *   - If value beyond `lbound` or `ubound` → `'abnormal'`
 *   - If only one bound is set (e.g., `ubound = -1`) and value exceeds `refmax` → `'warning'`
 *   - Handles cases where both `refmin` and `refmax` are `-1` and checks bounds directly
 *
 * For Non-Numeric:
 * - 'NEGATIVE' → `'normal'`
 * - 'POSITIVE' → `'abnormal'`
 * - 'TRACE'    → `''` (ignored)
 * - 'PRESENT...' (starts with) → `'abnormal'`
 *
 * @param {Object} ele - Lab result object.
 * @param {Object} ele.value - Contains the value and reference parameters:
 *   - {string|number} value - Actual test result
 *   - {string} reftype - Type of value ('NUMERIC' or other)
 *   - {number} refmin, refmax - Normal range boundaries
 *   - {number} lbound, ubound - Acceptable warning range boundaries
 * @returns {string} One of 'normal', 'warning', 'abnormal', or '' (unclassified or not applicable)
 */

export function GetLabResultClass(ele) {
    const rawValue = ele.value?.value !== undefined ? ele.value.value : '';
    const value = rawValue !== '' ? rawValue : '';
    const numericValue = rawValue !== '' ? parseFloat(value) : null;

    if (ele.value?.reftype?.toUpperCase() === 'NUMERIC') {
        if (ele.value?.lbound === -1 && ele.value?.ubound === -1) {
            if (ele.value.refmin !== -1 && ele.value.refmax !== -1 && numericValue >= ele.value.refmin && numericValue <= ele.value.refmax) {
                return 'normal';
            } else {
                return 'warning';
            }
        } else if (numericValue !== null && !isNaN(numericValue)) {
            if (ele.value.refmin !== -1 && ele.value.refmax !== -1 && numericValue >= ele.value.refmin && numericValue <= ele.value.refmax) {
                return 'normal';
            } else if (
                ele.value.refmin !== -1 &&
                ele.value.refmax !== -1 &&
                ((numericValue < ele.value.refmin && numericValue >= ele.value.lbound) ||
                    (numericValue > ele.value.refmax && numericValue <= ele.value.ubound))
            ) {
                return 'warning';
            } else if (ele.value.refmin === -1 && ele.value.refmax === -1 && numericValue >= ele.value.lbound && numericValue <= ele.value.ubound) {
                return 'normal';
            } else if ((ele.value.ubound == null || ele.value.ubound == -1) && numericValue > ele.value.refmax) {
                return 'warning';
            } else if ((ele.value.lbound == null || ele.value.lbound == -1) && numericValue < ele.value.refmin) {
                return 'warning';
            } else if (
                (ele.value.lbound !== 0 || ele.value.ubound !== 0) &&
                ((ele.value.lbound !== 0 && numericValue < ele.value.lbound) || (ele.value.ubound !== 0 && numericValue > ele.value.ubound))
            ) {
                return 'abnormal';
            }
        }
    } else {
        // Handle non-numeric (qualitative) cases
        const strValue = value?.toString().toUpperCase();

        if (strValue === 'NEGATIVE') return 'normal';
        if (strValue === 'POSITIVE') return 'abnormal';
        if (strValue === 'TRACE') return ''; // Skip TRACE

        if (strValue.startsWith('PRESENT')) return 'abnormal';
    }

    return ''; // Default or non-numeric
}
